import { useState, useMemo } from 'react';
import * as Yup from 'yup';
import { Form, TextInput, Textarea, Select } from '@mentimeter/ragnar-form';
import { tracking } from '@mentimeter/http-clients';
import { useUser } from '@mentimeter/user';
import { useToast } from '@mentimeter/toast';
import { trackUser } from '@api/tracking/client';
import { Text } from '@mentimeter/ragnar-ui/text';
import { Box } from '@mentimeter/ragnar-ui/box';
import { Button, useButtonState } from '@mentimeter/ragnar-ui/button';
import {
  CharacterCount,
  CharacterCountWrap,
} from '@mentimeter/ragnar-ui/character-count';
import {
  ModalBody,
  ModalContainer,
  ModalHeader,
  ModalRoot,
} from '@mentimeter/ragnar-ui/modal';
import { useTranslations } from 'next-intl';

interface ContactSalesModalProps {
  trackingContext: string;
  showSalesModal: boolean;
  setShowSalesModal: (showSalesModal: boolean) => void;
  showSubHeading?: boolean;
}

interface FormValuesT {
  name: string;
  email: string;
  licensesNumber: string;
  company: string;
  message: string;
  phoneNumber?: string | undefined;
  jobTitle: string;
}

const createMessage = ({
  name,
  email,
  company,
  jobTitle,
  licensesNumber,
  phoneNumber,
  message,
}: FormValuesT) => `
Name: ${name}
Email: ${email}
Job title: ${jobTitle}
Company name: ${company}
Phone number: ${phoneNumber}

Number of users to include in the subscription: ${licensesNumber}

Reference: IAEF2022

${message}`;

export const ContactSalesModal = ({
  trackingContext,
  showSalesModal,
  setShowSalesModal,
  showSubHeading,
}: ContactSalesModalProps) => {
  const t = useTranslations('purchasing.contact_sales_modal');
  const toast = useToast();
  const { user } = useUser();
  const [buttonState, setButtonState] = useButtonState();
  const [message, setMessage] = useState('');

  const licenses_selector_default_value = {
    key: 'defaultValue',
    value: '',
    label: t('contact_sales_modal_default_license_selector_value'),
    disabled: true,
  };

  const validationSchema: Yup.Schema<FormValuesT> = useMemo(
    () =>
      Yup.object().shape({
        name: Yup.string()
          .required(t('contact_sales_modal_name_required_message'))
          .max(150, t('contact_sales_modal_name_too_long_message')),
        email: Yup.string()
          .email()
          .required(t('contact_sales_modal_email_required_message'))
          .max(150, t('contact_sales_modal_email_too_long_message')),
        company: Yup.string()
          .required(t('contact_sales_modal_company_required_message'))
          .max(150, t('contact_sales_modal_company_too_long_message')),
        jobTitle: Yup.string()
          .required(t('contact_sales_modal_job_required_message'))
          .max(100, t('contact_sales_modal_job_too_long_message')),
        licensesNumber: Yup.string().required(
          t('contact_sales_modal_licenses_required_message'),
        ),
        phoneNumber: Yup.string(),
        message: Yup.string().required(
          t('contact_sales_modal_message_required_message'),
        ),
      }),
    [t],
  );

  const handleSuccess = () => {
    setButtonState('success');
    toast.displayToast({
      description: t('contact_sales_modal_success_message'),
      autoDismiss: true,
    });
    trackUser({
      event: 'Submitted contact sales form in-app',
      properties: { context: trackingContext },
    });
    setShowSalesModal(false);
  };

  const contactSales = async (formValues: FormValuesT) => {
    if (user?.email) {
      try {
        setButtonState('loading');
        const {
          data: { conversation_id: conversationId },
        } = await tracking().createConversation({
          email: user?.email,
          subject: 'Enquiry for Mentimeter Enterprise',
          message: createMessage(formValues),
        });
        await tracking().assignConversation({ conversationId });
        handleSuccess();
      } catch (e) {
        toast.displayToast({
          description: t('contact_sales_modal_error_message'),
          autoDismiss: false,
        });
      } finally {
        setButtonState(undefined);
      }
    }
  };

  return (
    <ModalRoot open={showSalesModal} onOpenChange={setShowSalesModal}>
      <ModalContainer>
        <ModalHeader>{t('contact_sales_modal_header_label')}</ModalHeader>
        <ModalBody>
          {showSubHeading && (
            <Text mb="space6">
              {t('contact_sales_modal_25_licenses_subheading_label')}
            </Text>
          )}
          <Form
            width="100%"
            initialValues={
              {
                name: user?.name,
                email: user?.email,
                licensesNumber: '',
                company: '',
                message: '',
              } as FormValuesT
            }
            validationSchema={validationSchema}
            onSubmit={contactSales}
          >
            <TextInput
              width="100%"
              name="name"
              aria-label={t('contact_sales_modal_name_input_label_a11y')}
              label={t('contact_sales_modal_name_input_label')}
            />
            <TextInput
              width="100%"
              name="jobTitle"
              aria-label={t('contact_sales_modal_job_title_input_label_a11y')}
              label={t('contact_sales_modal_job_title_input_label')}
            />
            <TextInput
              width="100%"
              name="company"
              aria-label={t('contact_sales_modal_company_input_label_a11y')}
              label={t('contact_sales_modal_company_input_label')}
            />
            <Select
              width="100%"
              name="licensesNumber"
              aria-label={t('contact_sales_modal_licenses_input_label_a11y')}
              label={t('contact_sales_modal_licenses_input_label')}
              options={[
                licenses_selector_default_value,
                ...['1-9', '10-20', '21-50', '51-99', '100+'].map((v) => ({
                  key: v,
                  value: v,
                  label: v,
                })),
              ]}
            />
            <TextInput
              width="100%"
              name="email"
              aria-label={t('contact_sales_modal_email_input_label_a11y')}
              label={t('contact_sales_modal_email_input_label')}
            />
            <TextInput
              width="100%"
              name="phoneNumber"
              hintText={t('contact_sales_modal_phone_input_hint_text')}
              aria-label={t('contact_sales_modal_phone_input_label_a11y')}
              label={t('contact_sales_modal_phone_input_label')}
            />

            <CharacterCountWrap width="100%" multiline mt={3}>
              <Textarea
                name="message"
                aria-describedby="message-character-counter"
                aria-label={t('contact_sales_modal_message_input_label_a11y')}
                label={t('contact_sales_modal_message_input_label')}
                rows={7}
                onChange={(e) => setMessage(e.target.value)}
                inputSize="compact"
              />
              <CharacterCount
                id="message-character-counter"
                maxLength={500}
                value={message}
                multiline
              />
            </CharacterCountWrap>
            <Box
              flexDirection="row"
              mt={3}
              justifyContent="flex-end"
              width="100%"
            >
              <Button
                onClick={() => setShowSalesModal(false)}
                variant="secondary"
                aria-label={t('contact_sales_modal_cancel_button_label_a11y')}
              >
                {t('contact_sales_modal_cancel_button_label')}
              </Button>
              <Button
                variant="primary"
                ml={2}
                aria-label={t('contact_sales_modal_send_button_label_a11y')}
                state={buttonState}
                type="submit"
              >
                {t('contact_sales_modal_send_button_label')}
              </Button>
            </Box>
          </Form>
        </ModalBody>
      </ModalContainer>
    </ModalRoot>
  );
};

export default ContactSalesModal;
